import {
  Box,
  Button,
  CardActions,
  CardContent,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Loading from "../../../../components/loading/loading";
import { AddRounded, DeleteOutlineRounded, EditRounded, SearchRounded, VisibilityRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { SurveyList } from "../../model/surveyList";
import { black, green, greenLight, purple, purpleLight, red, redLight } from "../../../../theme/colors";
import {
  removeSurveyor,
  serveyorList,
} from "../../../../services/surveyor/surveyor";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { queryClient } from "../../../../providers/query/query";
import ConfirmDialog from "../../../../components/confirm_dialog/confirm_dialog";
import useUser from "../../../../providers/user/user_provider";
import styled from "@emotion/styled";
import { useSurveyorsFilterStore } from "../../../../store/use-surveyors-filter-store";
import { useSelectedOrgStore } from "../../../../store/use-selected-org-store";
import OrgSelector from "../../../../components/org_selector/org_selector";
import { ToolbarButton } from "../../../../components/toolbar_button/toolbar_button";
import SortIcon from "../../../../assets/sort.svg";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import SortSurveyorsPopup, {
  DEFAULT_SURVEYORS_SORT_OPTION,
  SurveyorsSortOption,
  surveyorsSortOptionLabel,
  surveyorsSortOptionToParams,
} from "../sort_surveyors_popup/sort_surveyors_popup";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function SurveyorList() {
  const [selectedSurveyor, setSelectedSurveyor] = useState<SurveyList | null>(
    null
  );
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [sortOption, setSortOption] = useState<SurveyorsSortOption>(DEFAULT_SURVEYORS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);

  const { page, setPage, searchValue, setSearchValue } = useSurveyorsFilterStore();
  const { selectedOrgId } = useSelectedOrgStore();

  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const { hasPermission } = useUser();

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage + 1);
  };

  const { data: surveyorsListData, isLoading } = useQuery(
    ["surveyorListing", page, pageSize, debouncedSearchValue, sortOption, selectedOrgId],
    async () => {
      const { sortBy, sortOrder } = surveyorsSortOptionToParams(sortOption);
      let response = await serveyorList({
        page: page,
        size: pageSize,
        keyword: debouncedSearchValue,
        sortBy,
        sortOrder,
        orgId: selectedOrgId,
      });
      return response?.data?.data || [];
    }
  );

  const list: SurveyList[] = surveyorsListData?.list || [];
  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, surveyorsListData?.total || 0);
  const customText = `Showing ${surveyorsListData?.total ? startRange : 0} to ${endRange || 0} of ${surveyorsListData?.total || 0} entries`;

  function deleteSurveyor() {
    setDeleting(true);
    removeSurveyor({ id: selectedSurveyor?.id! })
      .then((res) => {
        showSnackbar("Surveyor deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["surveyorListing"] });
        setOpenDeletePopup(false);
      })
      .catch((e) => showSnackbar("Something went wrong"))
      .finally(() => setDeleting(false));
  }

  return (
    <>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search Surveyors"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ color: "text.primary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <OrgSelector lockToOwnOrg />
            <ToolbarButton
              icon={SortIcon}
              label={surveyorsSortOptionLabel(sortOption)}
              onClick={() => setSortDialogOpen(true)}
            />
            {hasPermission("surveyors", "create") && (
              <Button
                onClick={() => navigate("/surveyors/createNewSurveyor/")}
                variant="contained"
                disableElevation
                startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                sx={{
                  bgcolor: purple,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#7A2074" },
                }}
              >
                Add New Surveyor
              </Button>
            )}
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : list.length === 0 ? (
          <TableEmptyState
            message="No surveyors found"
            hint="Try adjusting your search, or add a new surveyor."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={[
                { label: "Sl. No.", flex: 0.3 },
                { label: "Surveyor ID", flex: 0.4 },
                { label: "Surveyor", flex: 2 },
                { label: "Actions" },
              ]}
              lastColumnWidth={150}
              align="left"
              bordered
            />
            {list.map((surveyor, index) => (
              <TableRow key={surveyor.id} bordered>
                <TableCell label="Sl. No." flex={0.3} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {(page - 1) * pageSize + index + 1}
                  </Typography>
                </TableCell>
                <TableCell label="Surveyor ID" flex={0.4} bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {surveyor?.id}
                  </Typography>
                </TableCell>
                <TableCell label="Surveyor" flex={2} bordered>
                  <Typography fontWeight={600} color={black} noWrap>
                    {surveyor?.name}
                  </Typography>
                  <Box display="flex" flexDirection="row" flexWrap="wrap" mt={0.25}>
                    <Typography variant="body2" color={purple} fontWeight={600} mr={1}>
                      {`${surveyor.choicesAssigned} Surveys mapped`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[surveyor.state, surveyor.district, surveyor.city].filter(Boolean).join(" - ")}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell isLast lastColumnWidth={150} bordered>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                    <Tooltip title="View details">
                      <IconButton
                        onClick={() => navigate(`/surveyors/viewSurveyorDetails/${surveyor.id}`)}
                        sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                      >
                        <VisibilityRounded sx={{ color: green, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    {hasPermission("surveyors", "update") && (
                      <Tooltip title="Edit surveyor">
                        <IconButton
                          onClick={() =>
                            navigate("/surveyors/modifySurveyorDetails/", { state: { id: surveyor?.id } })
                          }
                          sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                        >
                          <EditRounded sx={{ color: purple, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermission("surveyors", "delete") && (
                      <Tooltip title="Delete surveyor">
                        <IconButton
                          onClick={() => {
                            setSelectedSurveyor(surveyor);
                            setOpenDeletePopup(true);
                          }}
                          sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                        >
                          <DeleteOutlineRounded sx={{ color: red, fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {list.length > 0 && (
        <CardActions sx={{ px: 3, pb: 3, mt: "auto" }}>
          <Box width="100%">
            <TableFooterPagination
              count={surveyorsListData?.total || 0}
              page={page - 1}
              onPageChange={handlePageChange}
              summaryText={customText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
            />
          </Box>
        </CardActions>
      )}

      <SortSurveyorsPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />

      <ConfirmDialog
        open={openDeletePopup}
        onClose={() => setOpenDeletePopup(false)}
        onConfirm={deleteSurveyor}
        title="Delete Surveyor"
        message={`Are you sure you want to delete "${selectedSurveyor?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleting}
      />
    </>
  );
}
